import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  IoBusinessOutline,
  IoChatbubbleEllipsesOutline,
  IoChevronDown,
  IoGridOutline,
  IoHelpCircleOutline,
  IoLogOutOutline,
  IoMegaphoneOutline,
  IoPulseOutline,
} from 'react-icons/io5';
import { useReviewsNewCount } from '@modules/reviews';
import { useProductsModerationPendingCount } from '@modules/products';
import { sellerApplicationsModerationApi } from '@modules/seller-applications/api/seller-applications-moderation.api';
import { authApi } from '@modules/auth/api/auth.api';
import { authUtils } from '@shared/utils/axios-instance';
import { DoubleCheckModal } from '@shared/ui';
import { AdminNotificationsBell } from '@shared/components/AdminNotificationsBell/AdminNotificationsBell';
import { SellerSupportChatToastListener } from '@modules/seller-support-chat/components/SellerSupportChatToastListener';
import { sellerSupportChatApi } from '@modules/seller-support-chat/api/seller-support-chat.api';
import { CHAT_UNREAD_QUERY_KEY } from '@modules/seller-support-chat/chat-cache';
import { ClientSupportChatToastListener } from '@modules/client-support-chat/components/ClientSupportChatToastListener';
import { clientSupportChatApi } from '@modules/client-support-chat/api/client-support-chat.api';
import { CHAT_UNREAD_QUERY_KEY as CLIENT_CHAT_UNREAD_QUERY_KEY } from '@modules/client-support-chat/chat-cache';
import { SHOW_COLLECTIONS } from '@shared/constants/featureFlags';
import logoMark from '../../../assets/logo-mark.png';
import './Layout.css';

function readAdminRole(): 'SUPER_ADMIN' | 'SELLER_ADMIN' | null {
  try {
    const raw = localStorage.getItem('luxus_admin');
    if (!raw) return null;
    const p = JSON.parse(raw) as { role?: string };
    return p.role === 'SELLER_ADMIN' ? 'SELLER_ADMIN' : p.role === 'SUPER_ADMIN' ? 'SUPER_ADMIN' : null;
  } catch {
    return null;
  }
}

type CollapsibleNavGroupProps = {
  label: string;
  icon?: ReactNode;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
};

function CollapsibleNavGroup({ label, icon, open, onToggle, children }: CollapsibleNavGroupProps) {
  return (
    <div className={`sidebar-nav-group sidebar-nav-group--collapsible${open ? ' is-open' : ''}`}>
      <button
        type="button"
        className="sidebar-nav-group__toggle"
        onClick={onToggle}
        aria-expanded={open}
      >
        <span className="sidebar-nav-group__label">
          {icon ? <span className="sidebar-nav-group__icon">{icon}</span> : null}
          {label}
        </span>
        <IoChevronDown className="sidebar-nav-group__chevron" aria-hidden />
      </button>
      {open ? <ul className="sidebar-nav-group__list">{children}</ul> : null}
    </div>
  );
}

export default function Layout() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const adminRole = useMemo(() => readAdminRole(), [location.pathname]);
  const isSuperAdmin = adminRole !== 'SELLER_ADMIN';
  const { data: newReviewsData } = useReviewsNewCount();
  const newReviewsCount = newReviewsData?.count ?? 0;
  const { data: moderationPendingData } = useProductsModerationPendingCount(isSuperAdmin);
  const pendingProductsCount = moderationPendingData?.count ?? 0;
  const { data: pendingSellerApplications = [] } = useQuery({
    queryKey: ['seller-applications-moderation', 'PENDING_MODERATION', 'sidebar'],
    queryFn: () => sellerApplicationsModerationApi.list('PENDING_MODERATION'),
    enabled: isSuperAdmin,
  });
  const { data: pendingSellerProfileChanges = [] } = useQuery({
    queryKey: ['seller-profile-changes-moderation'],
    queryFn: () => sellerApplicationsModerationApi.listProfileChanges(),
    enabled: isSuperAdmin,
  });
  const pendingSellerApplicationsCount =
    pendingSellerApplications.length + pendingSellerProfileChanges.length;
  const { data: chatUnreadData } = useQuery({
    queryKey: CHAT_UNREAD_QUERY_KEY,
    queryFn: () => sellerSupportChatApi.getUnreadCount(),
    enabled: isSuperAdmin,
    staleTime: 0,
  });
  const chatUnreadCount = chatUnreadData?.count ?? 0;
  const { data: clientChatUnreadData } = useQuery({
    queryKey: CLIENT_CHAT_UNREAD_QUERY_KEY,
    queryFn: () => clientSupportChatApi.getUnreadCount(),
    enabled: isSuperAdmin,
    staleTime: 0,
  });
  const clientChatUnreadCount = clientChatUnreadData?.count ?? 0;

  const isActive = (path: string): boolean => {
    if (path === '/') {
      return location.pathname === '/';
    }
    if (path === '/orders') {
      const p = location.pathname;
      if (p === '/orders') return true;
      if (p.startsWith('/orders/') && !p.startsWith('/orders/category-stats')) return true;
      return false;
    }
    if (path === '/sellers') {
      const p = location.pathname;
      if (p === '/sellers') return true;
      if (p.startsWith('/sellers/') && !p.startsWith('/sellers/support-chat')) return true;
      return false;
    }
    return location.pathname.startsWith(path);
  };

  const catalogRouteActive =
    isActive('/sections') || isActive('/sets') || isActive('/options');
  const marketingRouteActive =
    isActive('/') ||
    isActive('/push') ||
    isActive('/bonuses') ||
    isActive('/orders/category-stats');

  const [catalogOpen, setCatalogOpen] = useState(catalogRouteActive);
  const [marketingOpen, setMarketingOpen] = useState(marketingRouteActive);

  useEffect(() => {
    if (catalogRouteActive) setCatalogOpen(true);
  }, [catalogRouteActive]);

  useEffect(() => {
    if (marketingRouteActive) setMarketingOpen(true);
  }, [marketingRouteActive]);

  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try {
      await authApi.logout();
    } catch {
      // Игнорируем ошибку — выходим локально в любом случае
    }
    await queryClient.cancelQueries();
    queryClient.clear();
    authUtils.removeAccessToken();
    localStorage.removeItem('luxus_admin');
    navigate('/login');
  };

  return (
    <div className="layout">
      {isSuperAdmin ? (
        <div className="layout-floating-notifications" aria-live="polite">
          <AdminNotificationsBell enabled />
        </div>
      ) : null}
      {isSuperAdmin ? <SellerSupportChatToastListener /> : null}
      {isSuperAdmin ? <ClientSupportChatToastListener /> : null}
      <div className="layout-container">
        <nav className="sidebar">
          <div className="sidebar-header">
            <div className="sidebar-brand">
              <img src={logoMark} alt="" className="sidebar-brand-logo" />
              <div className="sidebar-brand-text">
                <p className="sidebar-brand-eyebrow">Админка</p>
                <h2 className="sidebar-brand-title">Лавка Цветов</h2>
              </div>
            </div>
          </div>
          {isSuperAdmin ? (
            <>
              <div className="sidebar-menu">
                <div className="sidebar-nav-group">
                  <div className="sidebar-nav-group__label">
                    <span className="sidebar-nav-group__icon">
                      <IoPulseOutline />
                    </span>
                    Работа
                  </div>
                  <ul className="sidebar-nav-group__list">
                    <li>
                      <Link to="/orders" className={`menu-item ${isActive('/orders') ? 'active' : ''}`}>
                        Заказы
                      </Link>
                    </li>
                    <li>
                      <Link to="/reviews" className={`menu-item ${isActive('/reviews') ? 'active' : ''}`}>
                        Отзывы
                        {newReviewsCount > 0 && (
                          <span className="menu-item-badge" title="Новых отзывов">
                            {newReviewsCount > 99 ? '99+' : newReviewsCount}
                          </span>
                        )}
                      </Link>
                    </li>
                    <li>
                      <Link to="/products" className={`menu-item ${isActive('/products') ? 'active' : ''}`}>
                        Товары
                        {pendingProductsCount > 0 && (
                          <span className="menu-item-badge" title="Товаров на модерации">
                            {pendingProductsCount > 99 ? '99+' : pendingProductsCount}
                          </span>
                        )}
                      </Link>
                    </li>
                  </ul>
                </div>

                <div className="sidebar-nav-group">
                  <div className="sidebar-nav-group__label">
                    <span className="sidebar-nav-group__icon">
                      <IoBusinessOutline />
                    </span>
                    Платформа
                  </div>
                  <ul className="sidebar-nav-group__list">
                    <li>
                      <Link to="/sellers" className={`menu-item ${isActive('/sellers') ? 'active' : ''}`}>
                        Продавцы
                        {pendingSellerApplicationsCount > 0 && (
                          <span className="menu-item-badge" title="Заявок продавцов на модерации">
                            {pendingSellerApplicationsCount > 99
                              ? '99+'
                              : pendingSellerApplicationsCount}
                          </span>
                        )}
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/seller-finance"
                        className={`menu-item ${isActive('/seller-finance') ? 'active' : ''}`}
                      >
                        Финансы
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/marketplace-settings"
                        className={`menu-item ${isActive('/marketplace-settings') ? 'active' : ''}`}
                      >
                        Настройки
                      </Link>
                    </li>
                  </ul>
                </div>

                <CollapsibleNavGroup
                  label="Каталог"
                  icon={<IoGridOutline />}
                  open={catalogOpen}
                  onToggle={() => setCatalogOpen((v) => !v)}
                >
                  <li>
                    <Link to="/sections" className={`menu-item ${isActive('/sections') ? 'active' : ''}`}>
                      Категории
                    </Link>
                  </li>
                  {SHOW_COLLECTIONS ? (
                    <li>
                      <Link to="/sets" className={`menu-item ${isActive('/sets') ? 'active' : ''}`}>
                        Подборки
                      </Link>
                    </li>
                  ) : null}
                  <li>
                    <Link to="/options" className={`menu-item ${isActive('/options') ? 'active' : ''}`}>
                      Справочники
                    </Link>
                  </li>
                </CollapsibleNavGroup>

                <CollapsibleNavGroup
                  label="Маркетинг"
                  icon={<IoMegaphoneOutline />}
                  open={marketingOpen}
                  onToggle={() => setMarketingOpen((v) => !v)}
                >
                  <li>
                    <Link to="/" className={`menu-item ${isActive('/') ? 'active' : ''}`}>
                      Баннеры
                    </Link>
                  </li>
                  <li>
                    <Link to="/push" className={`menu-item ${isActive('/push') ? 'active' : ''}`}>
                      Рассылки
                    </Link>
                  </li>
                  <li>
                    <Link to="/bonuses" className={`menu-item ${isActive('/bonuses') ? 'active' : ''}`}>
                      Бонусная система
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/orders/category-stats"
                      className={`menu-item ${isActive('/orders/category-stats') ? 'active' : ''}`}
                    >
                      Заказы по категориям
                    </Link>
                  </li>
                </CollapsibleNavGroup>
              </div>

              <div className="sidebar-footer">
                <ul className="sidebar-footer-menu">
                  <li>
                    <Link
                      to="/sellers/support-chat"
                      className={`menu-item ${isActive('/sellers/support-chat') ? 'active' : ''}`}
                    >
                      <span className="menu-item__main">
                        <span className="menu-item__icon" aria-hidden>
                          <IoChatbubbleEllipsesOutline />
                        </span>
                        Чаты с продавцами
                      </span>
                      {chatUnreadCount > 0 && !isActive('/sellers/support-chat') ? (
                        <span className="menu-item-dot" title="Есть непрочитанные сообщения" />
                      ) : null}
                    </Link>
                  </li>
                  <li>
                    <Link
                      to="/clients/support-chat"
                      className={`menu-item ${isActive('/clients/support-chat') ? 'active' : ''}`}
                    >
                      <span className="menu-item__main">
                        <span className="menu-item__icon" aria-hidden>
                          <IoChatbubbleEllipsesOutline />
                        </span>
                        Чаты с клиентами
                      </span>
                      {clientChatUnreadCount > 0 && !isActive('/clients/support-chat') ? (
                        <span className="menu-item-dot" title="Есть непрочитанные сообщения" />
                      ) : null}
                    </Link>
                  </li>
                  <li>
                    <Link to="/help" className="menu-item">
                      <span className="menu-item__main">
                        <span className="menu-item__icon" aria-hidden>
                          <IoHelpCircleOutline />
                        </span>
                        Помощь
                      </span>
                    </Link>
                  </li>
                </ul>
                <button type="button" className="menu-item logout-btn" onClick={() => setLogoutConfirmOpen(true)}>
                  <span className="menu-item__main">
                    <span className="menu-item__icon" aria-hidden>
                      <IoLogOutOutline />
                    </span>
                    Выйти
                  </span>
                </button>
              </div>
            </>
          ) : (
            <>
              <ul className="sidebar-menu">
                <li className="menu-item menu-item-static">
                  Вход выполнен как продавец. Управление магазином — в приложении admin-seller (порт 3002).
                </li>
              </ul>
              <div className="sidebar-footer">
                <button type="button" className="menu-item logout-btn" onClick={() => setLogoutConfirmOpen(true)}>
                  <span className="menu-item__main">
                    <span className="menu-item__icon" aria-hidden>
                      <IoLogOutOutline />
                    </span>
                    Выйти
                  </span>
                </button>
              </div>
            </>
          )}
        </nav>
        <main className="main-content">
          <Outlet />
        </main>
      </div>
      <DoubleCheckModal
        isOpen={logoutConfirmOpen}
        onClose={() => {
          if (!logoutLoading) setLogoutConfirmOpen(false);
        }}
        title="Выйти из админ-панели?"
        message="Сессия завершится, и потребуется снова войти по email и паролю."
        firstButtonText="Отмена"
        secondButtonText="Выйти"
        secondButtonAction={() => {
          void handleLogout();
        }}
        isLoading={logoutLoading}
      />
    </div>
  );
}
